"use client";

import { useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { Frame, Verdict, Note } from "./parts";

const L = {
    fr: {
        title: "Le même stopPropagation, deux résultats selon la phase",
        hint: "Cliquez dans une boîte pour la viser, puis cochez la capture sans rien changer d'autre.",
        pick: "Viser cet élément",
        capture: "Écouteurs des ancêtres posés en capture",
        captureHint: "addEventListener(…, { capture: true })",
        stopOn: "stopPropagation() appelé sur",
        none: "Aucun",
        journal: "Ordre réel des déclenchements",
        p1: "capture",
        p2: "cible",
        p3: "remontée",
        offPath: "hors trajet",
        never: "jamais appelé",
        phaseWord: "eventPhase",
        linkText: "En savoir plus",
        names: ["page", "#liste", ".carte", "a"],
        targets: "event.target vaut {cible} sur chaque ligne appelée. Seul event.currentTarget change : il vaut l'élément de la ligne.",
        vBubble: "Les {total} écouteurs du trajet sont appelés : la cible d'abord, puis chaque ancêtre du plus proche au plus lointain. C'est le réglage par défaut d'addEventListener.",
        vCapture: "Les {total} écouteurs sont appelés dans l'ordre inverse : chaque ancêtre s'exécute avant la cible, du plus lointain au plus proche, et {cible} ferme la marche.",
        vBefore: "Coupé pendant la descente. {stop} arrête la course avant qu'elle atteigne la cible : l'écouteur de {cible} n'est jamais appelé, alors que le clic l'a bien visé. Décochez la capture, et ce même stopPropagation, posé au même endroit, laisse {cible} s'exécuter en premier.",
        vCut: "{appeles} écouteurs appelés sur {total}. Après {stop} la course s'arrête, et l'événement n'atteint jamais {restants}.",
        vCut1: "Un seul écouteur appelé sur {total}. Après {stop} la course s'arrête, et l'événement n'atteint jamais {restants}.",
        vLast: "{stop} est le dernier appelé du trajet : son stopPropagation n'a plus rien à couper. Les {total} écouteurs s'exécutent quand même.",
        vOff: "{stop} n'est pas un ancêtre de {cible}, c'est un élément situé plus bas. Son écouteur n'est jamais appelé, et son stopPropagation non plus.",
        vRoot: "La racine est aussi la cible : aucun ancêtre au-dessus, donc aucun trajet à parcourir. Un seul écouteur s'exécute, en phase cible, et la case capture n'y change rien.",
        note: "Le journal est calculé, pas capté sur de vrais écouteurs : il applique les règles du DOM à quatre éléments imbriqués. La case pose la capture sur tous les ancêtres à la fois, là où du vrai code la choisit écouteur par écouteur. Chaque boîte ne porte qu'un écouteur, ce qui laisse de côté stopImmediatePropagation, dont la différence n'apparaît que lorsqu'un même élément en porte plusieurs.",
    },
    en: {
        title: "The same stopPropagation, two outcomes depending on the phase",
        hint: "Click inside a box to aim at it, then check capture without changing anything else.",
        pick: "Aim at this element",
        capture: "Ancestor listeners registered in capture",
        captureHint: "addEventListener(…, { capture: true })",
        stopOn: "stopPropagation() called on",
        none: "None",
        journal: "Actual order of the calls",
        p1: "capture",
        p2: "target",
        p3: "bubbling",
        offPath: "off the path",
        never: "never called",
        phaseWord: "eventPhase",
        linkText: "Learn more",
        names: ["page", "#list", ".card", "a"],
        targets: "event.target equals {cible} on every line that runs. Only event.currentTarget changes: it equals the element of the line.",
        vBubble: "All {total} listeners on the path are called: the target first, then each ancestor from the nearest to the farthest. This is the default setting of addEventListener.",
        vCapture: "All {total} listeners are called in the opposite order: each ancestor runs before the target, from the farthest to the nearest, and {cible} is the last one to run.",
        vBefore: "Cut on the way down. {stop} stops the run before it reaches the target: the listener on {cible} is never called, although the click did aim at it. Uncheck capture and that very same stopPropagation, placed at the very same spot, lets {cible} run first.",
        vCut: "{appeles} listeners called out of {total}. After {stop} the run stops, and the event never reaches {restants}.",
        vCut1: "A single listener called out of {total}. After {stop} the run stops, and the event never reaches {restants}.",
        vLast: "{stop} is the last one called on the path: its stopPropagation has nothing left to cut. All {total} listeners run anyway.",
        vOff: "{stop} is not an ancestor of {cible}, it is an element sitting lower down. Its listener is never called, and neither is its stopPropagation.",
        vRoot: "The root is also the target: no ancestor above it, so no path to travel. A single listener runs, in the target phase, and the capture box changes nothing.",
        note: "The log is computed, not captured from real listeners: it applies the DOM rules to four nested elements. The box registers capture on every ancestor at once, where real code picks it listener by listener. Each box carries a single listener, which leaves aside stopImmediatePropagation, whose difference only shows when one element carries several.",
    },
};

const fill = (chaine, valeurs) =>
    chaine.replace(/\{(\w+)\}/g, (_, cle) => valeurs[cle]);

/**
 * Ordre des appels, tel que le DOM les produit sur la lignée
 * page > liste > carte > lien décrite en ouverture du terme.
 *
 * Capture : de la racine vers la cible, ancêtres seulement, eventPhase 1.
 * Cible : l'élément visé lui-même, eventPhase 2.
 * Remontée : de la cible vers la racine, ancêtres seulement, eventPhase 3.
 */
function sequence(cible, capture) {
    const ordre = [];
    if (capture) {
        for (let i = 0; i < cible; i++) ordre.push({ el: i, phase: 1 });
        ordre.push({ el: cible, phase: 2 });
    } else {
        ordre.push({ el: cible, phase: 2 });
        for (let i = cible - 1; i >= 0; i--) ordre.push({ el: i, phase: 3 });
    }
    return ordre;
}

export default function EventPhases({ lang = "fr" }) {
    const t = L[lang] || L.fr;
    const noms = t.names;

    const [cible, setCible] = useState(3);
    const [capture, setCapture] = useState(false);
    // Armé d'entrée sur #liste : sans stopPropagation posé, cocher la capture
    // ne réordonne que le journal. Avec, la même case fait basculer un résultat
    // binaire, la cible s'exécute ou ne s'exécute pas du tout.
    const [stop, setStop] = useState(1);

    const ordre = sequence(cible, capture);
    const coupe = stop === null ? -1 : ordre.findIndex((e) => e.el === stop);
    const appeles = coupe === -1 ? ordre.length : coupe + 1;
    const joues = ordre.slice(0, appeles);
    const cibleAppelee = joues.some((e) => e.el === cible);
    const restants = ordre.slice(appeles).map((e) => noms[e.el]);

    const nomPhase = (phase) => (phase === 1 ? t.p1 : phase === 2 ? t.p2 : t.p3);

    /**
     * Ni opacity ni fond teinté sur plusieurs niveaux : les boîtes sont
     * imbriquées, donc les deux se multiplient. Quatre opacity-45 empilées
     * mettaient la boîte intérieure à 9 % et la rendaient illisible ; quatre
     * fonds à 6 % donnaient une teinte 3,7 fois plus forte au centre, qui
     * n'encodait rien. La teinte ne marque plus que la cible, une seule fois.
     */
    const etat = (i) => {
        if (i > cible) return "border-gray-200 dark:border-white/10";
        if (!joues.some((e) => e.el === i))
            return "border-dashed border-gray-300 dark:border-white/15";
        return i === cible ? "border-primary/60 bg-primary/[0.06]" : "border-primary/60";
    };

    const badge = (i) => {
        if (i > cible) return t.offPath;
        if (i === cible) return t.p2;
        return capture ? t.p1 : t.p3;
    };

    const boite = (i, marge = "") => ({
        onClick: (event) => {
            event.stopPropagation();
            setCible(i);
        },
        className: `rounded-xl border p-3 cursor-pointer transition-colors duration-200 ${marge} ${etat(i)}`,
    });

    const entete = (i) => (
        <div className="flex items-center justify-between gap-2">
            <button
                type="button"
                aria-pressed={i === cible}
                aria-label={`${t.pick} ${noms[i]}`}
                onClick={(event) => {
                    event.stopPropagation();
                    setCible(i);
                }}
                className={`font-geistMono text-xs transition-colors duration-200 ${
                    i === cible
                        ? "text-primary font-bold"
                        : i > cible
                          ? "text-gray-400 hover:text-gray-700 dark:text-grayDark-500 dark:hover:text-grayDark-300"
                          : "text-gray-600 hover:text-gray-900 dark:text-lightBlue dark:hover:text-white"
                }`}
            >
                {noms[i]}
            </button>
            <span
                className={`text-[10px] uppercase tracking-widest shrink-0 ${
                    i > cible
                        ? "text-gray-400 dark:text-grayDark-500"
                        : i === cible
                          ? "text-primary"
                          : "text-gray-500 dark:text-lightBlue"
                }`}
            >
                {badge(i)}
            </span>
        </div>
    );

    const choix = (actif, contenu, onClick, cle) => (
        <button
            key={cle}
            type="button"
            onClick={onClick}
            className={`rounded-lg px-2.5 py-1.5 font-geistMono text-xs transition-colors duration-200 ${
                actif
                    ? "bg-primary text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-white/10 dark:text-grayDark-200 dark:hover:bg-white/20"
            }`}
        >
            {contenu}
        </button>
    );

    let ton = "good";
    let verdict = capture
        ? fill(t.vCapture, { total: cible + 1, cible: noms[cible] })
        : fill(t.vBubble, { total: cible + 1 });

    if (cible === 0 && (stop === null || stop === 0)) {
        ton = "neutral";
        verdict = t.vRoot;
    } else if (stop !== null && coupe === -1) {
        ton = "neutral";
        verdict = fill(t.vOff, { stop: noms[stop], cible: noms[cible] });
    } else if (stop !== null && !cibleAppelee) {
        ton = "bad";
        verdict = fill(t.vBefore, { stop: noms[stop], cible: noms[cible] });
    } else if (stop !== null && restants.length > 0) {
        ton = "warn";
        verdict = fill(appeles === 1 ? t.vCut1 : t.vCut, {
            appeles,
            total: cible + 1,
            stop: noms[stop],
            restants: restants.join(", "),
        });
    } else if (stop !== null) {
        ton = "neutral";
        verdict = fill(t.vLast, { stop: noms[stop], total: cible + 1 });
    }

    return (
        <Frame icon="transfer-vertical-linear" title={t.title}>
            <p className="text-sm text-gray-600 dark:text-lightBlue mb-4">{t.hint}</p>

            <div {...boite(0)}>
                {entete(0)}
                <div {...boite(1, "mt-2")}>
                    {entete(1)}
                    <div {...boite(2, "mt-2")}>
                        {entete(2)}
                        <div {...boite(3, "mt-2")}>
                            {entete(3)}
                            <span className="mt-1 block text-sm text-primary underline">
                                {t.linkText}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-5 space-y-4">
                <label className="flex items-start gap-2.5 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={capture}
                        onChange={(event) => setCapture(event.target.checked)}
                        className="mt-0.5 w-4 h-4 shrink-0 accent-primary cursor-pointer"
                    />
                    <span>
                        <span className="block text-sm dark:text-white">{t.capture}</span>
                        <span className="block font-geistMono text-xs text-gray-500 dark:text-lightBlue/70">
                            {t.captureHint}
                        </span>
                    </span>
                </label>

                <div>
                    <div className="text-sm text-gray-600 dark:text-lightBlue mb-2">
                        {t.stopOn}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {choix(stop === null, t.none, () => setStop(null), "none")}
                        {noms.map((nom, i) => choix(stop === i, nom, () => setStop(i), nom))}
                    </div>
                </div>
            </div>

            <div className="mt-6">
                <div className="text-sm text-gray-600 dark:text-lightBlue mb-2">
                    {t.journal}
                </div>
                <div className="rounded-xl border border-gray-200/70 dark:border-white/10 divide-y divide-gray-100 dark:divide-white/10">
                    {ordre.map((e, i) => (
                        <div key={`${e.el}-${e.phase}`}>
                            <div
                                className={`flex items-center gap-2 px-3 py-2 ${
                                    i < appeles ? "" : "opacity-45"
                                }`}
                            >
                                <span className="w-4 shrink-0 text-xs tabular-nums text-gray-400 dark:text-grayDark-500">
                                    {i < appeles ? i + 1 : "·"}
                                </span>
                                <span className="flex-1 min-w-0 font-geistMono text-sm dark:text-white">
                                    {noms[e.el]}
                                </span>
                                <span className="shrink-0 text-xs text-gray-500 dark:text-lightBlue">
                                    {nomPhase(e.phase)}
                                </span>
                                <span className="w-[5.5rem] shrink-0 text-right text-xs tabular-nums text-gray-400 dark:text-grayDark-500">
                                    {i < appeles ? `${t.phaseWord} ${e.phase}` : t.never}
                                </span>
                            </div>
                            {i === coupe && i < ordre.length - 1 ? (
                                <div className="flex items-center gap-2 bg-primary/[0.06] px-3 py-1.5">
                                    <SolarIcon
                                        name="scissors-square-linear"
                                        className="w-4 h-4 shrink-0 text-primary"
                                    />
                                    <span className="font-geistMono text-xs text-primary">
                                        stopPropagation()
                                    </span>
                                </div>
                            ) : null}
                        </div>
                    ))}
                </div>
                <p className="mt-2 mb-0 text-xs text-gray-500 dark:text-lightBlue/70">
                    {fill(t.targets, { cible: noms[cible] })}
                </p>
            </div>

            <Verdict tone={ton}>{verdict}</Verdict>
            <Note>{t.note}</Note>
        </Frame>
    );
}
